<template>
  <div class="container-fluid bg-black text-light py-4 min-vh-100">
    <!-- 2 columns -->
    <div class="row g-3">
      <!-- Left column -->
      <div class="col-12 col-lg-6">
        <!-- Factory widget -->
        <div class="card bg-dark text-white mb-3">
          <div class="card-body">
            <div class="h5 mb-3">🏭 Factory</div>

            <table class="table table-dark table-sm mb-0">
              <thead class="bg-secondary text-white">
              <tr>
                <th>Metric</th>
                <th class="text-end">Total USDT</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Assets</td>
                <td class="text-end text-success">{{ formatChfStyle(factoryAssetsUsdt) }}</td>
              </tr>
              <tr>
                <td>Profit</td>
                <td class="text-end text-success">{{ formatChfStyle(factoryProfitUsdt) }}</td>
              </tr>
              </tbody>
            </table>

            <div class="text-muted small mt-2">
              Assets = somme des Futures wallets (factory1..factory10) · Profit = BingX main Funding wallet
            </div>
          </div>
        </div>

        <!-- Copy accounts -->
        <div class="card bg-dark text-white mb-3">
          <div class="card-body">
            <div class="h5 mb-3">👥 Copy trading</div>

            <table class="table table-dark table-sm mb-0">
              <thead class="bg-secondary text-white">
              <tr>
                <th>Exchange</th>
                <th class="text-end">Total USDT</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="a in copyAccounts" :key="a.key">
                <td>{{ a.exchange }}</td>
                <td class="text-end text-success">{{ formatChfStyle(a.total_usdt) }}</td>
              </tr>
              <tr v-if="copyAccounts.length === 0">
                <td colspan="2" class="text-center text-muted py-3">—</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Standard accounts -->
        <div class="card bg-dark text-white">
          <div class="card-body">
            <div class="h5 mb-3">🏦 Standard</div>

            <table class="table table-dark table-sm mb-0">
              <thead class="bg-secondary text-white">
              <tr>
                <th>Exchange</th>
                <th class="text-end">Total USDT</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="a in standardAccounts" :key="a.key">
                <td>{{ a.exchange }}</td>
                <td class="text-end text-success">{{ formatChfStyle(a.total_usdt) }}</td>
              </tr>
              <tr v-if="standardAccounts.length === 0">
                <td colspan="2" class="text-center text-muted py-3">—</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="col-12 col-lg-6">
        <div class="card bg-dark text-white h-100">
          <div class="card-body">
            <div class="h5 mb-3">🤖 Bots</div>

            <table class="table table-dark table-sm mb-0">
              <thead class="bg-secondary text-white">
              <tr>
                <th>Exchange</th>
                <th class="text-end">Total USDT</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="a in botAccounts" :key="a.key">
                <td>{{ a.exchange }}</td>
                <td class="text-end text-success">{{ formatChfStyle(a.total_usdt) }}</td>
              </tr>
              <tr v-if="botAccounts.length === 0">
                <td colspan="2" class="text-center text-muted py-3">—</td>
              </tr>
              </tbody>
            </table>

            <div class="text-muted small mt-3">
              Note: totals are merged across all wallets for each account.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Full width placeholder chart -->
    <div class="card bg-dark text-white mt-4">
      <div class="card-body">

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="h5 mb-0">📈 Evolution USDT</div>

          <div class="d-flex gap-2">
            <select v-model="selectedAccountId" class="form-select form-select-sm bg-black text-light border-secondary">
              <option value="all">All accounts</option>
              <option v-for="acc in accounts" :key="acc.id" :value="String(acc.id)">
                {{ acc.exchange }} — {{ acc.name }}
              </option>
            </select>

            <select v-model="bucket" class="form-select form-select-sm bg-black text-light border-secondary">
              <option :value="300">5m</option>
              <option :value="900">15m</option>
              <option :value="3600">1h</option>
            </select>

            <!-- ✅ new: currency -->
            <select v-model="currency" class="form-select form-select-sm bg-black text-light border-secondary">
              <option value="USDT">USDT</option>
              <option value="CHF">CHF</option>
            </select>
          </div>
        </div>

        <WalletEvolutionChart :series="displayedHistory" />

      </div>
    </div>


  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import WalletEvolutionChart from "@/components/WalletEvolutionChart.vue";

// ✅ IMPORTANT: walletBalances manquait
import { walletBalances, walletHistory, requestWalletHistory, settings } from "@/state";

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

export default {
  components: { WalletEvolutionChart },

  setup() {
    const selectedAccountId = ref("all");
    const bucket = ref(300); // 5m default
    const hours = ref(168);  // 7 days default

    // ✅ new: currency selector
    const currency = ref("USDT"); // "USDT" | "CHF"

    // ✅ new: series convertie pour le chart
    const chfRate = computed(() =>
        Number(settings.value.fx_usdt_chf) || 0.8
    );
    const displayedHistory = computed(() => {
      const src = walletHistory.value || [];
      if (currency.value === "CHF") {
        return src.map(p => ({
          time: p.time,
          value: Number(p.value) * chfRate.value
        }));
      }
      return src;
    });

    // Build a unique list of accounts from current balances (for the select)
    const accounts = computed(() => {
      const map = new Map();
      for (const w of walletBalances.value || []) {
        const key = `${w.exchange}|${w.account_name}`;
        if (!map.has(key)) {
          map.set(key, {
            id: w.account_id,
            exchange: w.exchange,
            name: w.account_name,
          });
        }
      }
      return [...map.values()];
    });

    function isFactoryBotAccountName(name) {
      return typeof name === "string" && /^factory\d+$/i.test(name.trim());
    }

    const factoryAssetsUsdt = computed(() => {
      let sum = 0;
      for (const r of rows.value) {
        if (r.account_type !== "factory") continue;
        if (!isFactoryBotAccountName(r.account_name)) continue;
        sum += getRowTotalUsdt(r);
      }
      return sum;
    });

    const factoryProfitUsdt = computed(() => {
      let sum = 0;
      for (const r of rows.value) {
        if (r.account_type !== "factory") continue;
        if (r.exchange !== "bingx") continue;
        if (String(r.account_name) !== "bingx_main") continue;
        if (String(r.scope) !== "fund") continue;
        sum += getRowTotalUsdt(r);
      }
      return sum;
    });


    // ✅ This is "the requestWalletHistory part" you want in Dashboard.vue:
    function reloadHistory() {
      requestWalletHistory({
        accountId: selectedAccountId.value,
        bucketSec: bucket.value,
        hours: hours.value,
      });
    }

    onMounted(() => {
      reloadHistory();
    });

    // Reload when the user changes account or bucket (and hours, if you ever add a UI for it)
    watch([selectedAccountId, bucket, hours], () => {
      reloadHistory();
    });

    const rows = computed(() => walletBalances.value || []);

    function formatChfStyle(x) {
      const n = Number(x);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 0 }).format(n);
    }

    function getRowTotalUsdt(r) {
      // Preferred: backend sends total_usdt
      const v = safeNumber(r.total_usdt);
      if (v != null) return v;

      // Minimal fallback for stablecoins only
      if (r.asset === "USDT" || r.asset === "USDC") {
        const t = safeNumber(r.total);
        return t != null ? t : 0;
      }

      return 0;
    }

    function aggregateByAccount(type) {
      const map = new Map();

      for (const r of rows.value) {
        if (r.account_type !== type) continue;

        const key = `${r.exchange}|${r.account_name}`;
        const prev = map.get(key) || {
          key,
          exchange: r.exchange,
          account_name: r.account_name,
          total_usdt: 0,
        };

        prev.total_usdt += getRowTotalUsdt(r);
        map.set(key, prev);
      }

      return [...map.values()].sort((a, b) => {
        const d = b.total_usdt - a.total_usdt;
        if (d !== 0) return d;
        return a.exchange.localeCompare(b.exchange);
      });
    }

    const copyAccounts = computed(() => aggregateByAccount("copy"));
    const standardAccounts = computed(() => aggregateByAccount("standard"));
    const botAccounts = computed(() => aggregateByAccount("bot"));

    return {
      // tables
      copyAccounts,
      standardAccounts,
      botAccounts,
      formatChfStyle,

      // ✅ IMPORTANT: exposer ce que le template utilise
      selectedAccountId,
      bucket,
      hours,
      accounts,
      walletHistory,      // optionnel si tu ne l’utilises plus directement
      displayedHistory,   // ✅ chart series
      factoryAssetsUsdt,
      factoryProfitUsdt,
      currency,
    };
  },
};
</script>

